// TODO-electron-post-release-bedirhan: Add keyboard navigation support
// TODO-electron-tech-debt-bedirhan: Refactor this component to make it more generic. Then move it under Section scope
import { h, Fragment } from "preact";
import { useState, useRef, useLayoutEffect } from "preact/hooks";

import { SortTypes } from "../../foundation/enums";
import { populateArrayOfConstantIncrements } from "../../foundation/utils/array";
import classname from "../../foundation/utils/classname";

import DragDropContainer from "../DragDropContainer";
import Elevator from "../Elevator";
import { useDidMountWillUnmountEffect } from "../hooks";
import { useSectionObserver } from "../useSectionObserverHook";

import { DragDropSectionElevatorItem } from "./SectionElevatorItem";
import { NESTED_SECTION_INDENTATION } from "./specs";
import { ElevatorSection } from "./types";

import styles from "./SectionElevator.module.css";

interface Props {
    class?: string;
    sections: ElevatorSection[];
    selectedSortType: SortTypes;
    icon?: JSX.Element;
    hideItemCount?: boolean;
    reorderEnabled: boolean;
    maximumDepthLimit?: number;
    animation?: "slide" | "fade";
    shouldAdaptPosition?: boolean;
    hoveredSectionId?: string;
    hoveredSectionDepth?: number;
    onSectionClick: (sectionId: string) => void;
    onSectionReorder: ({ sourceId, sourceIndex, moveIndex, source }: {
        sourceId: string;
        sourceIndex: number;
        moveIndex: number;
        source?: string;
    }) => void;
}

const SectionElevatorDivider = () => <hr class={styles.sectionElevatorDivider}/>;

const NON_ALPHABET_SLOT = "#";
const INTERSECTION_THRESHOLD = 100; // 76 (FilterSortView height) + 24 (Section padding bottom)
const ELEVATOR_PADDING = 4;
const SECTION_ITEM_HEIGHT = 32;
const DIVIDER_HEIGHT = 5;

/**
 * Return the divider index if there is an item in the list to be separated from others
 * @param sections section list
 * @param selectedSortType selected sort type
 * @returns {number} Can be either -1 (no divider), 1 (divide after the first), length - 1 (divide before the last)
 */
function getDividerIndex(sections: ElevatorSection[], selectedSortType: SortTypes) {
    if (selectedSortType === SortTypes.SECTIONS) {
        // In the project dashboard, divide after the first item if the default section exists
        if (sections[0].type === "default") {
            return shouldDisplaySection(sections[0]) ? 1 : -1;
        }

        // In the workspace, divide before the last item if the individual projects section exists
        const lastSectionIndex = sections.length - 1;
        if (sections[lastSectionIndex].type === "individual") {
            return shouldDisplaySection(sections[lastSectionIndex]) ? lastSectionIndex : -1;
        }
    }

    // If the sort type is A-Z and there are items of which names start with a non-alphabet character,
    // divide after the first item
    if (selectedSortType === SortTypes.NAME && sections[0].name === NON_ALPHABET_SLOT) {
        return 1;
    }

    return -1;
}

function shouldDisplaySection(section: ElevatorSection) {
    // Hide default sections if they are empty
    // TODO: find a better way to handle section types
    return (section.type !== "individual" || !!section.projects?.length) &&
        (section.type !== "default" || !!section.screens?.length);
}

function SectionElevator({
    class: cssClass,
    sections = [],
    selectedSortType,
    icon,
    hideItemCount,
    reorderEnabled,
    maximumDepthLimit = 0,
    animation,
    shouldAdaptPosition,
    hoveredSectionId: initialHoveredSectionId,
    hoveredSectionDepth = 0,
    onSectionClick,
    onSectionReorder
}: Props) {
    const [hoveredSectionId, setHoveredSectionId] = useState(initialHoveredSectionId);
    const [open, setOpen] = useState(false);
    const [dragMode, setDragMode] = useState(false);
    const [draggingSectionId, setDraggingSectionId] = useState("");
    const [highlightedSection, setHighlightedSection] = useState<string | null>(null);
    const [parentToHighlight, setParentToHighlight] = useState<string | null>(null);
    const {
        initSectionObserver,
        disconnectSectionObserver
    } = useSectionObserver() as {
        initSectionObserver: (observer: IntersectionObserver) => void;
        disconnectSectionObserver: () => void;
    };
    const scrollableDropContainer = useRef<HTMLDivElement | null>(null);
    const visibleSectionsRef = useRef(new Set());
    const renderTimeoutRef = useRef<number>(null);
    const draggingSection = sections.find(item => item._id === draggingSectionId);
    const draggingSectionDepth = (draggingSection?.path || []).length;
    const draggingSectionSubsections = sections.filter(({ path }) => (path || []).includes(draggingSectionId));
    const distanceToFarestLeaf = draggingSectionSubsections.length
        ? Math.max(...draggingSectionSubsections.map(({ path }) => (path || []).length - draggingSectionDepth))
        : 0;
    const computedMaximumDepthLimit = Math.max(maximumDepthLimit - distanceToFarestLeaf, 0);
    const draggingSectionIds = draggingSection ? [
        draggingSection._id,
        ...draggingSectionSubsections.map(draggingSectionSubsection => draggingSectionSubsection._id)
    ] : [];

    // Since we need the up-to-date version of sections in handleSectionVisibilityChange, we need to keep it in a ref
    // For more info, visit https://reactjs.org/docs/hooks-faq.html#why-am-i-seeing-stale-props-or-state-inside-my-function
    const sectionsRef = useRef(sections);

    if (!sections.length) {
        return null;
    }

    const defaultSectionTypes = new Set(["default", "individual"]);
    const defaultSection = sections.find(section => defaultSectionTypes.has(section.type));
    const dividerIndex = getDividerIndex(sections, selectedSortType);
    const hoveredSectionIndexOffset = defaultSection && !shouldDisplaySection(defaultSection) ? 1 : 0;
    const hoveredSectionIndex = sections.findIndex(item => item._id === hoveredSectionId) - hoveredSectionIndexOffset;
    const dividerHeight = dividerIndex === 1 && hoveredSectionIndex >= dividerIndex ? DIVIDER_HEIGHT : 0;
    const hoveredItemDistanceToTop = (
        ELEVATOR_PADDING +
        dividerHeight +
        hoveredSectionIndex * SECTION_ITEM_HEIGHT +
        SECTION_ITEM_HEIGHT / 2
    );

    function handleOpenElevator() {
        setOpen(true);
    }

    function handleCloseElevator() {
        if (!dragMode) {
            setOpen(false);
        }
    }

    function handleStartDrag() {
        setDragMode(true);
    }

    function handleEndDrag() {
        setDragMode(false);
        setDraggingSectionId("");
    }

    function handleSectionClick(id: string) {
        onSectionClick(id);
        handleCloseElevator();
    }

    function handleSectionStartDrag(id: string) {
        setHoveredSectionId(id);
    }

    function handleScrollContainerRef(el: HTMLDivElement | null) {
        scrollableDropContainer.current = el;
    }

    function getScrollableDropContainers() {
        return [scrollableDropContainer.current!];
    }

    function handleSectionVisibilityChange(entries: IntersectionObserverEntry[]) {
        let visibleSectionsChanged = false;
        const { current: visibleSections } = visibleSectionsRef;
        const { current: sectionList } = sectionsRef;

        entries.forEach(entry => {
            const {
                isIntersecting,
                target,
                intersectionRatio,
                intersectionRect: {
                    height: visibleHeight
                }
            } = entry;
            const sectionId = (target as HTMLElement).dataset["id"];

            const entryInTheList = visibleSections.has(sectionId);
            const hasEnoughIntersection = isIntersecting &&
                (intersectionRatio === 1.0 || visibleHeight > INTERSECTION_THRESHOLD);

            if (hasEnoughIntersection && !entryInTheList) {
                visibleSections.add(sectionId);
                visibleSectionsChanged = true;
            } else if (!hasEnoughIntersection && entryInTheList) {
                visibleSections.delete(sectionId);
                visibleSectionsChanged = true;
            }
        });

        // If nothing has changed, return
        if (!visibleSectionsChanged) {
            return;
        }

        // sections array may change, purge visible sections that are not present in the new sections array
        visibleSections.forEach(visibleSectionId => {
            if (!sectionList.some(section => section._id === visibleSectionId)) {
                visibleSections.delete(visibleSectionId);
            }
        });

        // Find a candidate with the lowest index
        const candidateSection = sectionList.find(section => visibleSections.has(section._id));

        // If the candidate is different from what we had, set it as the first visible section
        if (candidateSection && highlightedSection !== candidateSection._id) {
            window.clearTimeout(renderTimeoutRef.current);
            renderTimeoutRef.current = window.setTimeout(() => {
                setHighlightedSection(candidateSection._id);
            }, 80);
        }
    }

    useDidMountWillUnmountEffect(() => {
        const threshold = populateArrayOfConstantIncrements(0, 1.0, 20);

        initSectionObserver(new IntersectionObserver(handleSectionVisibilityChange, { threshold }));

        return function unmount() {
            disconnectSectionObserver();
        };
    });

    // We want this code to execute before painting and intersection changes take place
    // Therefore we use useLayoutEffect, not useEffect
    useLayoutEffect(() => {
        sectionsRef.current = sections;
        visibleSectionsRef.current = new Set();
    }, [sections]);

    function renderItems() {
        let firstDraggableItemFound = false;

        return sections.map((section, index) => {
            if (!shouldDisplaySection(section)) {
                return null;
            }

            const isDraggable = reorderEnabled && section.type !== "individual" && section.type !== "default";
            let allowDropToTop = false;
            let droppable = true;
            let minimumDepthLimit = 0;

            // Allow dropping to top for only the first draggable item
            if (!firstDraggableItemFound && isDraggable) {
                firstDraggableItemFound = true;
                allowDropToTop = true;
            }

            const nextSection = sections[index + 1];

            if (nextSection) {
                minimumDepthLimit = (nextSection.path || []).length;
            }

            const isDragging = draggingSectionIds.includes(section._id);

            if ((
                isDragging &&
                nextSection &&
                draggingSectionIds.includes(nextSection._id)) ||
                minimumDepthLimit > computedMaximumDepthLimit
            ) {
                droppable = false;
            }

            return (
                <Fragment key={`sectionElevatorItem-${section._id}`}>
                    {index === dividerIndex && <SectionElevatorDivider/>}
                    <DragDropSectionElevatorItem
                        index={index + 1}
                        dragMode={dragMode}
                        itemHeight={SECTION_ITEM_HEIGHT}
                        allowDropToTop={allowDropToTop}
                        minimumDepthLimit={minimumDepthLimit}
                        maximumDepthLimit={isDragging
                            ? Math.min(draggingSectionDepth, computedMaximumDepthLimit)
                            : computedMaximumDepthLimit}
                        draggingSectionsData={isDragging ? {
                            isFirst: draggingSectionIds.indexOf(section._id) === 0,
                            isLast: draggingSectionIds.indexOf(section._id) === draggingSectionIds.length - 1
                        } : undefined}
                        droppable={droppable}
                        setDraggingSectionId={setDraggingSectionId}
                        hideItemCount={hideItemCount}
                        reorderEnabled={isDraggable}
                        section={section}
                        highlight={(hoveredSectionId || highlightedSection) === section._id}
                        highlightAsParent={parentToHighlight === section._id}
                        shouldAdaptPosition={shouldAdaptPosition}
                        onClick={handleSectionClick}
                        onStartDrag={handleSectionStartDrag}
                        onSectionDrop={onSectionReorder}
                        onParentToHighlightChange={setParentToHighlight}/>
                </Fragment>
            );
        });
    }

    return (
        <DragDropContainer
            key="elevatorDragDropContainer"
            class={cssClass}
            itemsClass="elevatorItems"
            getScrollableDropContainers={getScrollableDropContainers}
            onStartDrag={handleStartDrag}
            onEndDrag={handleEndDrag}>
            <Elevator
                open={open}
                class={classname(styles.sectionElevator, { [styles.narrow]: selectedSortType === SortTypes.NAME })}
                dragMode={dragMode}
                animation={animation}
                shouldAdaptPosition={shouldAdaptPosition}
                hoveredItemDistanceToTop={hoveredItemDistanceToTop}
                icon={icon}
                leftOffset={ELEVATOR_PADDING + (hoveredSectionDepth + 1) * NESTED_SECTION_INDENTATION}
                onOpen={handleOpenElevator}
                onContentRef={handleScrollContainerRef}
                onClose={handleCloseElevator}>
                {renderItems()}
            </Elevator>
        </DragDropContainer>
    );
}

export default SectionElevator;
export { Props as SectionElevatorProps };
